import { useEffect, useMemo } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, usePreloadedQuery } from "react-relay";
import { PopupContentType } from "scmp-sdk/components/acquisition-popup-container/types";
import { useSdkContext } from "scmp-sdk/components/contexts";

import { gtag } from "shared/lib/gtag";

import type { displayAcquisitionPopupContainerQuery } from "scmp-app/queries/__generated__/displayAcquisitionPopupContainerQuery.graphql";

import type { useAcquisitionPopupOptions } from "./hooks";
import { useAcquisitionPopup } from "./hooks";
import { useRenderPopup } from "./popup-render-hooks";
import { useAcquisitionNewsletterPopupGetter } from "./popups/acquisition-newsletter/hooks";
import { useCampaignPopupGetter } from "./popups/campaign/hooks";
import { useMyNewsCompletionPopupGetter } from "./popups/my-news/completion/hooks";
import { useTopicPackageFollowPopupGetter } from "./popups/topic-package-follow/hooks";
import { useWhyRegisterPopupGetter } from "./popups/why-register/hooks";
import type { useRenderPopupOptions } from "./types";

export type Props = useAcquisitionPopupOptions &
  useRenderPopupOptions & {
    queryReference: PreloadedQuery<displayAcquisitionPopupContainerQuery>;
  };

export const AcquisitionPopupContainerDisplay: React.FunctionComponent<Props> = ({
  configEnvironmentSlug,
  queryReference,
  ...renderPopupOptions
}) => {
  const { currentRouteWithoutQuerystring } = useSdkContext();
  const data = usePreloadedQuery<displayAcquisitionPopupContainerQuery>(query, queryReference);
  useAcquisitionPopup({ configEnvironmentSlug }, data);

  useEffect(() => {
    if (currentRouteWithoutQuerystring) gtag("event", "optimize.triggerCampaign");
  }, [currentRouteWithoutQuerystring]);

  const campaignPopupGetter = useCampaignPopupGetter(renderPopupOptions);

  const acquisitionNewsletterPopupGetter = useAcquisitionNewsletterPopupGetter(renderPopupOptions);

  const whyRegisterPopupGetter = useWhyRegisterPopupGetter(renderPopupOptions);

  const topicPackageFollowPopupGetter = useTopicPackageFollowPopupGetter(renderPopupOptions);

  // My news
  const myNewsCompletionPopupGetter = useMyNewsCompletionPopupGetter(renderPopupOptions);

  /**
   * the following is mapping of each popup
   * the render function will loop for each key for popup content until the first non-undefined content
   * or return empty fragment if no more popup content is found
   */
  const popupGetters = useMemo(
    () => ({
      [PopupContentType.AcquisitionNewsletter]: acquisitionNewsletterPopupGetter,
      [PopupContentType.Campaign]: campaignPopupGetter,
      [PopupContentType.MyNewsCompletion]: myNewsCompletionPopupGetter,
      [PopupContentType.TopicPackageFollow]: topicPackageFollowPopupGetter,
      [PopupContentType.WhyRegister]: whyRegisterPopupGetter,
    }),
    [
      acquisitionNewsletterPopupGetter,
      campaignPopupGetter,
      myNewsCompletionPopupGetter,
      topicPackageFollowPopupGetter,
      whyRegisterPopupGetter,
    ],
  );

  const { popup } = useRenderPopup(popupGetters, renderPopupOptions);

  return popup?.element ?? null;
};

AcquisitionPopupContainerDisplay.displayName = "AcquisitionPopupContainerDisplay";

export const query = graphql`
  query displayAcquisitionPopupContainerQuery {
    ...hooksUseAcquisitionPopupQuery
  }
`;
